import type { CSSProperties, RefObject } from 'react';

export const getReflectionState = (ref: RefObject<HTMLElement>) => {
  const parentElement = ref.current;

  const bottom = parentElement?.getBoundingClientRect().bottom || 0;
  const right = parentElement?.getBoundingClientRect().right || 0;

  let reflectX = false,
    reflectY = false;
  if (typeof window !== 'undefined') {
    const screenCenter = [window.innerWidth / 2, window.innerHeight / 2];
    if (right > screenCenter[0]) reflectX = true;
    if (bottom > screenCenter[1]) reflectY = true;
  }

  return { x: reflectX, y: reflectY };
};

const DEFAULT_OFFSET_X = 0;
const DEFAULT_OFFSET_Y = -40;
const DEFAULT_REFLECT_X = false;
const DEFAULT_REFLECT_Y = true;

const useFixed = (
  ref: RefObject<HTMLElement>,
  pos?: PosOptionT,
): CSSProperties => {
  const parentElement = ref.current;
  return getFixedPosition(parentElement, pos);
};

export const getFixedPosition = (
  parentElement: HTMLElement | null,
  pos?: PosOptionT,
) => {
  const top = parentElement?.getBoundingClientRect().top || 0;
  const bottom = parentElement?.getBoundingClientRect().bottom || 0;
  const left = parentElement?.getBoundingClientRect().left || 0;
  const right = parentElement?.getBoundingClientRect().right || 0;

  if (typeof window === 'undefined') {
    return {};
  }

  const xOffset =
    typeof pos?.offset?.x === 'undefined' ? DEFAULT_OFFSET_X : pos?.offset?.x;
  const yOffset =
    typeof pos?.offset?.y === 'undefined' ? DEFAULT_OFFSET_Y : pos?.offset?.y;

  const reflectX =
    typeof pos?.reflect?.x === 'undefined'
      ? DEFAULT_REFLECT_X
      : !!pos?.reflect?.x;
  const reflectY =
    typeof pos?.reflect?.y === 'undefined'
      ? DEFAULT_REFLECT_Y
      : !!pos?.reflect?.y;

  const screenCenter = [window.innerWidth / 2, window.innerHeight / 2];
  if (reflectX && reflectY) {
    if (right < screenCenter[0]) {
      if (bottom < screenCenter[1])
        return {
          left: `${left + xOffset}px`,
          top: `${bottom + 10 + yOffset}px`,
        };
      else
        return {
          left: `${left + xOffset}px`,
          bottom: `${window.innerHeight - top + 10 + yOffset}px`,
        };
    } else {
      if (bottom < screenCenter[1])
        return {
          right: `${window.innerWidth - right + xOffset}px`,
          top: `${bottom + 10 + yOffset}px`,
        };
      else
        return {
          right: `${window.innerWidth - right + xOffset}px`,
          bottom: `${window.innerHeight - top + 10 + yOffset}px`,
        };
    }
  } else if (!reflectX && !reflectY) {
    return {
      left: `${left + xOffset}px`,
      top: `${bottom + 10 + yOffset}px`,
    };
  } else if (reflectX && !reflectY) {
    if (right < screenCenter[0]) {
      return {
        left: `${left + xOffset}px`,
        top: `${bottom + 10 + yOffset}px`,
      };
    } else {
      return {
        right: `${window.innerWidth - right + xOffset}px`,
        top: `${bottom + 10 + yOffset}px`,
      };
    }
  } else if (!reflectX && reflectY) {
    if (bottom < screenCenter[1]) {
      return {
        left: `${left + xOffset}px`,
        top: `${bottom + 10 + yOffset}px`,
      };
    }
    return {
      left: `${left + xOffset}px`,
      bottom: `${window.innerHeight - top + 10 + yOffset}px`,
    };
  }

  return {};
};

export const useAbsolute = (
  ref: RefObject<HTMLElement>,
  yOffset?: number,
  xOffset?: number,
  autoReflectPosition: boolean = true,
): CSSProperties => {
  const parentElement = ref.current;

  const bottom = parentElement?.getBoundingClientRect().bottom || 0;
  const right = parentElement?.getBoundingClientRect().right || 0;

  const height = parentElement?.getBoundingClientRect().height || 0;

  if (typeof window === 'undefined') {
    return {};
  }

  if (autoReflectPosition) {
    const screenCenter = [window.innerWidth / 2, window.innerHeight / 2];

    if (right < screenCenter[0]) {
      if (bottom < screenCenter[1])
        return {
          left: `${xOffset || 0}px`,
          top: `${height + (yOffset || 0)}px`,
        };
      else
        return {
          left: `${xOffset || 0}px`,
          bottom: `${height + (yOffset || 0)}px`,
        };
    } else {
      if (bottom < screenCenter[1])
        return {
          right: `${xOffset || 0}px`,
          top: `${height + (yOffset || 0)}px`,
        };
      else
        return {
          right: `${xOffset || 0}px`,
          bottom: `${height + (yOffset || 0)}px`,
        };
    }
  } else
    return {
      left: `${xOffset || 0}px`,
      top: `${height + (yOffset || 0)}px`,
    };
};

export const getRealignableAbsolutePos = (
  ref: RefObject<HTMLElement>,
  yOffset?: number,
  xOffset?: number,
): CSSProperties => {
  const element = ref.current;

  const bottom = element?.getBoundingClientRect().bottom || 0;
  const right = element?.getBoundingClientRect().right || 0;

  if (typeof window === 'undefined') return {};

  const screenEdge = [window.innerWidth, window.innerHeight];

  if (right < screenEdge[0]) {
    if (bottom < screenEdge[1]) {
      return {
        left: `${xOffset || 0}px`,
        top: `${yOffset || 0}px`,
      };
    } else {
      // bottom > screenEdge[1]
      return {
        left: `${xOffset || 0}px`,
        bottom: `${yOffset || 0}px`,
      };
    }
  } else {
    // right >= screenEdge[0]
    if (bottom < screenEdge[1]) {
      return {
        right: `${xOffset || 0}px`,
        top: `${yOffset || 0}px`,
      };
    } else {
      // bottom > screenEdge[1]
      return {
        right: `${xOffset || 0}px`,
        bottom: `${yOffset || 0}px`,
      };
    }
  }
};

export const calcFixedWithID = (
  id: string,
  yOffset?: number,
  xOffset?: number,
  autoReflectPosition: boolean = true,
): CSSProperties => {
  const parentElement = document.getElementById(id);

  const top = parentElement?.getBoundingClientRect().top || 0;
  const bottom = parentElement?.getBoundingClientRect().bottom || 0;
  const left = parentElement?.getBoundingClientRect().left || 0;
  const right = parentElement?.getBoundingClientRect().right || 0;

  if (typeof window === 'undefined') {
    return {};
  }

  if (autoReflectPosition) {
    const screenCenter = [window.innerWidth / 2, window.innerHeight / 2];

    if (right < screenCenter[0]) {
      if (bottom < screenCenter[1])
        return {
          left: `${left + (xOffset !== undefined ? xOffset : 0)}px`,
          top: `${bottom + 10 + (yOffset !== undefined ? yOffset : 0)}px`,
        };
      else
        return {
          left: `${left + (xOffset !== undefined ? xOffset : 0)}px`,
          bottom: `${
            window.innerHeight -
            top +
            10 +
            (yOffset !== undefined ? yOffset : 0)
          }px`,
        };
    } else {
      if (bottom < screenCenter[1])
        return {
          right: `${
            window.innerWidth - right + (xOffset !== undefined ? xOffset : 0)
          }px`,
          top: `${bottom + 10 + (yOffset !== undefined ? yOffset : 0)}px`,
        };
      else
        return {
          right: `${
            window.innerWidth - right + (xOffset !== undefined ? xOffset : 0)
          }px`,
          bottom: `${
            window.innerHeight -
            top +
            10 +
            (yOffset !== undefined ? yOffset : 0)
          }px`,
        };
    }
  } else
    return {
      left: `${left + (xOffset !== undefined ? xOffset : 0)}px`,
      top: `${bottom + 10 + (yOffset !== undefined ? yOffset : 0)}px`,
    };
};

export default useFixed;
